/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useEffect } from "react";
import ContentLayout from "../layouts/ContentLayout";
import AppText from "../other/AppText";
import { IDict } from "../other/constants";
import { AppButton } from "../buttons/AppButton";
import GoogleMapComponent from "./GoogleMapComponent";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useBreadcrumbsLabel } from "@/store/clientStore";
import Image from "next/image";
import CustomLink from "../other/CustomLink";
import { SquareArrowOutUpRight } from "lucide-react";

interface IBranchHeaderCards {
  key: string;
  id: string;
  value: string | string[];
  icon: string;
  isPhone?: boolean;
}
interface IBranchHeader {
  id: string;
  label: string;
  heading: string;
  headingColoured: string;
  subheading: string;
  primaryCtaLabel: string;
  primaryCtaLink: string;
  secondaryCtaLabel: string;
  secondaryCtaLink: string;
  location: string;
  cards: IBranchHeaderCards[];
  dict: IDict;
  customData: any;
}

const BranchHeader = ({
  id,
  label,
  heading,
  headingColoured,
  subheading,
  primaryCtaLabel,
  primaryCtaLink,
  secondaryCtaLabel,
  secondaryCtaLink,
  cards,
  dict,
  customData,
}: IBranchHeader) => {
  const { lat, lng } = customData.coordinates || {};
  const { setBranchName } = useBreadcrumbsLabel();

  useEffect(() => {
    if (customData && customData.name) {
      setBranchName(customData.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customData]);

  const handleTel = () => {
    window.location.href = secondaryCtaLink;
  };

  const _label = label.split(" | ");

  return (
    <ContentLayout
      id={id}
      bgColorClass="bg-navy-blue"
      innerClass="lg:pr-0 lg:pl-[80px] xl:pr-0 xl:pl-[80px] 2xl:pr-[104px] 2xl:pl-[104px] overflow-hidden"
    >
      <div className="lg:flex-row sm:gap-8 lg:gap-6 xl:gap-10 lg:items-center flex flex-col gap-10">
        <div className="flex flex-col flex-1">
          <AppText type="LABEL_LARGE" className="text-mellow whitespace-break-spaces mb-2">
            {_label.length === 2 ? (
              <>
                {_label[0]} <span className="h-full text-[#FCFCFC] opacity-20 text-20">|</span> {_label[1]}
              </>
            ) : (
              label
            )}
          </AppText>

          <AppText type="HEADLINE_LARGE" className="flex flex-col">
            <span className="whitespace-break-spaces text-white">{heading}</span>
            <span className="whitespace-break-spaces text-dijon-80 mb-4">{headingColoured}</span>
          </AppText>

          {/* <AppText type="HEADLINE_LARGE" as="h2" className="whitespace-break-spaces text-dijon-80 mb-4">
            {headingColoured}
          </AppText> */}

          <AppText type="SUB_HEADING_SMALL" as="p" className="whitespace-break-spaces mb-8 text-white">
            {subheading}
          </AppText>

          <div className="sm:flex-row flex flex-col gap-8">
            <AppButton link={primaryCtaLink} intent="primary" label={primaryCtaLabel} bg="dark" />

            {secondaryCtaLabel && (
              <AppButton intent="secondary" onClick={handleTel} label={secondaryCtaLabel} bg="dark" />
            )}
          </div>
        </div>

        {lat && lng && (
          <div
            className={`flex-1 max-w-full lg:max-w-[50%] w-full overflow-hidden max-h-[391px] 
          sm:max-h-[344px] lg:max-h-[348px] xl:max-h-[430px] xxl:max-h-[455px] aspect-[1.67]`}
          >
            <APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API!}>
              <GoogleMapComponent
                center={{ lat, lng }}
                branches={[{ ...customData, lat, lng }]}
                zoom={9}
                moreDetails={dict.moreDetails}
                nearestMarkers={{}}
              />
            </APIProvider>
          </div>
        )}
      </div>

      <div className="xl:gap-10 lg:grid-cols-12 sm:grid-cols-6 sm:mt-20 grid grid-cols-4 gap-6 mt-16 text-white">
        {cards.map((card: IBranchHeaderCards) => {
          if (card.value === "" || card.value === null || typeof card.value === "undefined") {
            return <React.Fragment key={card.id}></React.Fragment>;
          }

          return card.isPhone ? (
            <CustomLink
              href={`tel:${card.value}`}
              key={card.id}
              className="flex flex-col bg-[#FBFBFB0D] px-6 py-4 gap-2 col-span-4 sm:col-span-3 lg:col-span-3"
            >
              <div className="flex items-center gap-4">
                <Image src={card.icon} alt="phone icon" width={20} height={20} />

                <AppText type="BODY_MEDIUM_EMPHASIS" className=" text-white">
                  {card.key}
                </AppText>
              </div>

              <div>
                <AppText type="BODY_SMALL_EMPHASIS" className=" flex items-center gap-2 text-white">
                  {card.value} <SquareArrowOutUpRight size={14} arial-label="open outside" />
                </AppText>
              </div>
            </CustomLink>
          ) : (
            <div
              key={card.id}
              className="flex flex-col bg-[#FBFBFB0D] px-6 py-4 gap-2 col-span-4 sm:col-span-3 lg:col-span-3"
            >
              <div className="flex items-center gap-4">
                <Image src={card.icon} alt="icon" width={20} height={20} />

                <AppText type="BODY_MEDIUM_EMPHASIS" className=" text-white">
                  {card.key}
                </AppText>
              </div>

              <div>
                {typeof card.value === "string" ? (
                  <AppText type="BODY_SMALL_EMPHASIS" className=" text-white">
                    {card.value}
                  </AppText>
                ) : (
                  <div className="flex flex-col">
                    {(card.value || []).map((item: string) => (
                      <AppText key={item} type="BODY_SMALL_EMPHASIS" className=" text-white">
                        {item}
                      </AppText>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ContentLayout>
  );
};

export default BranchHeader;
